import { useQuery } from "@tanstack/react-query";
import { useLocale } from "./useLocale";

interface Market {
  id: string;
  question: string;
  groupItemTitle?: string | null;
  slug: string;
  conditionId: string;
  eventStartTime?: string | null;
  priceToBeat?: number | null;
  resolutionSource?: string | null;
  outcomes: string[];
  outcomePrices: number[];
  clobTokenIds: string[];
  lastTradePrice?: number;
  bestBid?: number;
  bestAsk?: number;
  acceptingOrders: boolean;
  active: boolean;
  closed: boolean;
  volume: number;
  liquidity: number;
  openInterest?: number;
  enableOrderBook: boolean;
  image?: string | null;
  imageUrl?: string | null;
  endDate?: string | null;
  oneDayPriceChange?: number | null;
  oneHourPriceChange?: number | null;
  rewardsMaxSpread?: number;
  rewardsMinSize?: number;
  rewardsRatePerDay?: number;
  /** Polymarket Holding Rewards flag — when any market in the event has
   * this set to `true`, the chart footer renders the "Earn 4%" badge
   * (the 4% rate is a platform-wide constant on Polymarket too). */
  holdingRewardsEnabled?: boolean;
}

export interface EventData {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  image: string | null;
  icon: string | null;
  startDate: string | null;
  endDate: string | null;
  closed: boolean;
  active: boolean;
  volume: number;
  volume24hr: number;
  liquidity: number;
  openInterest?: number;
  oneDayPriceChange: number | null;
  competitive: number | null;
  markets: Market[];
  tags: any[];
  source?: string;
  mutuallyExclusive?: boolean;
  collateralReturnType?: string;
  settlementSources?: Array<{ name: string; url: string }>;
  seriesTicker?: string | null;
  seriesSlug?: string | null;
  /** Polymarket series objects — MarketTalks needs `series[0].id` for
   * series-level comments when the event itself has none. */
  series?: Array<{
    id: string;
    slug?: string | null;
    ticker?: string | null;
    title?: string | null;
    recurrence?: string | null;
  }>;
  marketVariant?: string | null;
}

// Exported so hover-prefetch sites (EventCard, TrendingMarkets) can warm
// the cache with the EXACT same payload shape the live query stores —
// critical for kalshi, which transforms the raw proxy response below.
// Prefetching with a raw `res.json()` would poison the cache with a
// different shape once the prefetch key is aligned to the live one.
export async function fetchEventData(slug: string, source: "polymarket" | "kalshi" | "predict"): Promise<EventData> {
  if (source === "predict") {
    // Predict data is transformed at SSR time — client refetch via backend proxy
    const response = await fetch(`/api/markets/predict/${encodeURIComponent(slug)}`);
    if (!response.ok) throw new Error("Failed to fetch Predict event data");
    const raw = await response.json();
    const data = raw.market || raw;
    const markets = (data.markets || []).map((m: any) => {
      const yesPrice = (m.chancePercentage || 50) / 100;
      return {
        id: m.id, question: m.title || m.question || '', slug: m.id || '',
        conditionId: m.conditionId || m.id, outcomes: (m.outcomes || []).map((o: any) => o.name),
        outcomePrices: [yesPrice, 1 - yesPrice],
        clobTokenIds: (m.outcomes || []).map((o: any) => o.onChainId || ''),
        acceptingOrders: m.isTradingEnabled !== false,
        active: m.isTradingEnabled !== false || (m.status || '').toUpperCase() === 'OPEN',
        closed: m.isTradingEnabled === false && (m.status || '').toUpperCase() !== 'OPEN',
        volume: m.volume || parseFloat(m.statistics?.volumeTotalUsd || '0'),
        liquidity: m.liquidity || parseFloat(m.statistics?.totalLiquidityUsd || '0'),
        enableOrderBook: true, image: null,
        decimalPrecision: m.decimalPrecision ?? 0,
      };
    });
    return { id: data.id || slug, slug: data.slug || slug, title: data.title || '',
      description: data.description || '', image: data.imageUrl || null, endDate: data.endsAt || null,
      closed: (data.status || '').toUpperCase() !== 'OPEN', active: (data.status || '').toUpperCase() === 'OPEN',
      volume: data.volume || data.statistics?.volumeTotalUsd || markets.reduce((s: number, m: any) => s + (m.volume || 0), 0),
      volume24hr: data.volume24hr || data.statistics?.volume24hUsd || markets.reduce((s: number, m: any) => s + (m.volume24hr || 0), 0),
      liquidity: data.liquidity || data.statistics?.liquidityValueUsd || markets.reduce((s: number, m: any) => s + (m.liquidity || 0), 0),
      markets, tags: data.tags || [],
      marketVariant: data.marketVariant || null } as EventData;
  }
  if (source === "kalshi") {
    const response = await fetch(`/api/markets/kalshi/event?ticker=${encodeURIComponent(slug)}`);
    if (!response.ok) throw new Error("Failed to fetch Kalshi event data");
    const kalshiData = await response.json();
    return {
      id: kalshiData.eventTicker,
      slug: kalshiData.eventTicker?.toLowerCase(),
      title: kalshiData.title,
      description: kalshiData.markets?.[0]?.rules || kalshiData.subtitle || null,
      image: kalshiData.imageUrl || kalshiData.featuredImageUrl || null,
      icon: null,
      startDate: kalshiData.startDate || null,
      endDate: kalshiData.markets?.[0]?.closeTime || null,
      closed: kalshiData.markets?.every((m: any) => m.status === "closed" || m.status === "settled" || m.status === "finalized"),
      active: kalshiData.markets?.some((m: any) => m.status === "open" || m.status === "active"),
      volume: kalshiData.markets?.reduce((sum: number, m: any) => sum + (m.volume || 0), 0) || 0,
      volume24hr: kalshiData.markets?.reduce((sum: number, m: any) => sum + (m.volume24h || 0), 0) || 0,
      liquidity: kalshiData.markets?.reduce((sum: number, m: any) => sum + (m.liquidity || 0), 0) || 0,
      openInterest: kalshiData.markets?.reduce((sum: number, m: any) => sum + (m.openInterest || 0), 0) || 0,
      oneDayPriceChange: null,
      competitive: null,
      markets: (kalshiData.markets || []).map((m: any) => ({
        id: m.ticker,
        question: m.title || m.ticker,
        groupItemTitle: m.title || null,
        slug: m.ticker,
        conditionId: m.ticker,
        outcomes: ["Yes", "No"],
        outcomePrices: [m.yesPrice || 0.5, m.noPrice || 0.5],
        clobTokenIds: [],
        lastTradePrice: m.lastPrice || m.yesPrice || null,
        bestBid: m.yesBid || null,
        bestAsk: m.yesAsk || null,
        acceptingOrders: m.status === "open" || m.status === "active",
        active: m.status === "open" || m.status === "active",
        closed: m.status === "closed" || m.status === "settled" || m.status === "finalized",
        endDate: m.closeTime || null,
        volume: m.volume || 0,
        liquidity: m.liquidity || 0,
        openInterest: m.openInterest || 0,
        enableOrderBook: false,
        // Per-market images: prefer the kalshi-public-docs flag/team image
        // (always real); only fall back to the event image when nothing is
        // set, never overwriting a real per-market url.
        imageUrl: m.imageUrl || kalshiData.imageUrl || kalshiData.featuredImageUrl || null,
        structuredTargetId: m.structuredTargetId || m.teamId || null,
        teamId: m.teamId || null,
        abbreviation: m.abbreviation || null,
        shortName: m.shortName || null,
        oneDayPriceChange: m.priceChange24h || null,
        oneHourPriceChange: null,
      })),
      tags: kalshiData.category ? [kalshiData.category] : [],
      source: "kalshi",
      mutuallyExclusive: kalshiData.mutuallyExclusive ?? true,
      collateralReturnType: kalshiData.collateralReturnType || '',
      settlementSources: kalshiData.settlementSources || [],
      seriesTicker: kalshiData.seriesTicker || null,
    };
  }

  // Polymarket
  const response = await fetch(`/api/polymarket/event/${slug}`);
  if (!response.ok) throw new Error("Failed to fetch event data");
  return response.json();
}

/**
 * React Query hook for event page data.
 * Uses initialData from SSR to avoid redundant refetch on mount.
 * Background revalidation happens after staleTime (30s).
 */
export function useEventQuery(
  slug: string,
  source: "polymarket" | "kalshi" | "predict",
  initialData?: EventData | null,
) {
  const locale = useLocale();
  return useQuery<EventData, Error>({
    // Locale folded into the cache key so flipping EN ↔ ES via the
    // header selector triggers a refetch (the proxy auto-injects
    // `?locale=` from the cookie, and gamma-api returns translated
    // `title`/`question`/`outcomes`). Without this, React Query
    // would keep serving the previous locale's payload from cache.
    queryKey: ["event-page", source, slug, locale],
    queryFn: () => fetchEventData(slug, source),
    enabled: !!slug,
    // Drop initialData when the user is on ES — the SSR-rendered EN
    // payload would flash before the locale-aware refetch landed.
    // Refetch is fast (Redis) so this is barely a delay.
    // When the query is disabled (no slug — e.g. Hyperliquid which has no
    // refetch endpoint), we MUST keep initialData regardless of locale, or
    // the consumer ends up with `undefined` data and renders the not-found
    // screen. The locale-drop only matters when there's a refetch path.
    initialData: !slug ? (initialData ?? undefined) : (locale === "en" ? (initialData ?? undefined) : undefined),
    staleTime: 5 * 60_000, // 5min — SSR data is fresh, avoid redundant refetch
    gcTime: 5 * 60_000,  // 5min cache
    retry: false,
    // Poll while a market in this event is in "awaiting resolution"
    // limbo — closed=true on gamma but outcomePrices still mid-range
    // (oracle hasn't pushed final 1/0 yet). 5m crypto up/down markets
    // typically settle within ~30-60s after close, but gamma's snapshot
    // refresh lag means our cached data sits at "closed but no winner"
    // until something invalidates. Polling every 8s closes that gap so
    // the trading panel rotates to "Resolved · Winner" on its own
    // without forcing a hard refresh.
    refetchInterval: (query) => {
      // Polymarket limbo is driven by EventClient's dedicated ?fresh=1 loop
      // (the 5min proxy cache makes a plain refetch here useless for
      // settlement — it returns the stale blob). Returning a non-false
      // interval would just double-poll. Kalshi/Predict have no such loop, so
      // this refetchInterval stays their sole resolution mechanism.
      if (source === "polymarket") return false;
      const data: EventData | undefined = query.state.data;
      if (!data?.markets?.length) return false;
      const inLimbo = data.markets.some((m) => {
        const closed = !!m.closed || (typeof m.endDate === 'string' && new Date(m.endDate).getTime() <= Date.now());
        if (!closed) return false;
        const prices = Array.isArray(m.outcomePrices) ? m.outcomePrices : [];
        if (prices.length !== 2) return false;
        return !prices.some((p) => typeof p === 'number' && p >= 0.99);
      });
      return inLimbo ? 8_000 : false;
    },
  });
}
